import type { ComponentType, ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { AlertTriangle, CalendarClock, CheckCircle2, Clock, Banknote, Users, Wallet } from 'lucide-react';
import { financeV2Service } from '../services/financeV2Service';
import { parseDbTimestamp } from '../utils/datetime';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { KpiGrid } from '@/components/ui/KpiGrid';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AmountText } from '@/components/finance-v2/FinanceBadges';
import { cn } from '@/utils/cn';

type Priority = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';

const PRIORITY_BADGES: Record<Priority, { variant: 'outline' | 'secondary' | 'default' | 'destructive'; label: string }> = {
  LOW: { variant: 'outline', label: 'Baixa' },
  MEDIUM: { variant: 'secondary', label: 'Média' },
  HIGH: { variant: 'default', label: 'Alta' },
  CRITICAL: { variant: 'destructive', label: 'Crítica' },
};

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

const formatDate = (dateString: string) => {
  const parsed = parseDbTimestamp(dateString);
  return parsed ? format(parsed, 'dd/MM/yyyy', { locale: ptBR }) : '-';
};

function StatCard({ label, total, count, icon: Icon, alert = false }: {
  label: string;
  total: number;
  count: number;
  icon: ComponentType<{ className?: string }>;
  alert?: boolean;
}) {
  return (
    <Card size="sm" className={cn(alert && 'ring-destructive/30')}>
      <CardContent className="flex items-center gap-3">
        <Icon className={cn('size-5 shrink-0', alert ? 'text-destructive' : 'text-muted-foreground')} aria-hidden="true" />
        <div className="min-w-0">
          <p className="truncate text-xs font-medium text-muted-foreground">{label}</p>
          <AmountText tone={alert ? 'expense' : 'neutral'} className="block truncate text-lg font-semibold">
            {formatCurrency(total)}
          </AmountText>
          <p className="text-xs text-muted-foreground">{count} transações</p>
        </div>
      </CardContent>
    </Card>
  );
}

function SectionCard({ title, icon: Icon, aside, children }: {
  title: string;
  icon: ComponentType<{ className?: string }>;
  aside?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Card className="min-w-0">
      <CardHeader className="flex flex-wrap items-center justify-between gap-2">
        <CardTitle className="flex items-center gap-2">
          <Icon className="size-4 text-muted-foreground" aria-hidden="true" />
          <h2>{title}</h2>
        </CardTitle>
        {aside}
      </CardHeader>
      <CardContent className="min-w-0">{children}</CardContent>
    </Card>
  );
}

function EmptyState({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-2 py-8 text-center">
      <CheckCircle2 className="size-8 text-muted-foreground" aria-hidden="true" />
      <p className="text-sm text-muted-foreground">{children}</p>
    </div>
  );
}

function ClientCell({ name, document }: { name?: string; document?: string }) {
  return (
    <TableCell className="whitespace-normal">
      <div className="text-sm font-medium">{name || 'Sem cliente'}</div>
      <div className="text-xs text-muted-foreground">{document}</div>
    </TableCell>
  );
}

function DescriptionCell({ description, category }: { description: string; category?: string }) {
  return (
    <TableCell className="whitespace-normal">
      <div className="text-sm">{description}</div>
      <div className="text-xs text-muted-foreground">{category}</div>
    </TableCell>
  );
}

export default function FinanceV2CollectionPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['collection-dashboard'],
    queryFn: () => financeV2Service.reports.getCollectionDashboard(),
    refetchInterval: 60000, // Atualiza a cada 1 minuto
  });

  const dashboard = data?.data;

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4 p-4 sm:p-6" aria-busy="true">
        <span className="sr-only">Carregando...</span>
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {[0, 1, 2].map((i) => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 p-4 sm:p-6">
      <div className="flex min-w-0 flex-col gap-1">
        <div className="flex items-center gap-2">
          <Banknote className="size-6 shrink-0 text-muted-foreground" />
          <h1 className="text-2xl font-semibold">Dashboard de Cobrança</h1>
        </div>
        <p className="text-sm text-muted-foreground">Gestão ativa de inadimplência e cobranças</p>
      </div>

      {dashboard?.statistics && (
        <KpiGrid cols={3} gap={3}>
          <StatCard
            label="Valores Vencidos"
            total={dashboard.statistics.overdue.total}
            count={dashboard.statistics.overdue.count}
            icon={AlertTriangle}
            alert={dashboard.statistics.overdue.count > 0}
          />
          <StatCard
            label="Vencendo em 7 dias"
            total={dashboard.statistics.dueSoon.total}
            count={dashboard.statistics.dueSoon.count}
            icon={CalendarClock}
          />
          <StatCard
            label="Total Pendente"
            total={dashboard.statistics.pending.total}
            count={dashboard.statistics.pending.count}
            icon={Wallet}
          />
        </KpiGrid>
      )}

      {dashboard?.topDebtors && dashboard.topDebtors.length > 0 && (
        <SectionCard title="Principais Devedores" icon={Users}>
          <div className="flex flex-col gap-2">
            {dashboard.topDebtors.map((debtor) => (
              <div key={debtor.clientId} className="flex flex-col gap-2 rounded-lg border p-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0 flex-1">
                  <p className="break-words font-medium">{debtor.clientName}</p>
                  <p className="text-sm text-muted-foreground">
                    {debtor.overdueCount} parcelas vencidas • {debtor.maxDaysOverdue} dias
                  </p>
                </div>
                <div className="text-left sm:text-right">
                  <AmountText tone="expense" className="text-lg font-semibold">{formatCurrency(debtor.overdueTotal)}</AmountText>
                  <p className="text-xs text-muted-foreground">Mais antigo: {formatDate(debtor.oldestDueDate)}</p>
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      <SectionCard
        title="Valores Vencidos"
        icon={AlertTriangle}
        aside={<span className="text-xs text-muted-foreground sm:text-sm">Prioridade automática</span>}
      >
        {dashboard?.overdue && dashboard.overdue.length > 0 ? (
          <div className="min-w-0 overflow-x-auto">
            <Table className="min-w-[720px]">
              <TableHeader>
                <TableRow>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Descrição</TableHead>
                  <TableHead>Vencimento</TableHead>
                  <TableHead>Atraso</TableHead>
                  <TableHead>Valor</TableHead>
                  <TableHead>Prioridade</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {dashboard.overdue.slice(0, 20).map((item) => {
                  const priority = PRIORITY_BADGES[item.priority];
                  return (
                    <TableRow key={item.id}>
                      <ClientCell name={item.clientName} document={item.clientDocument} />
                      <DescriptionCell description={item.description} category={item.categoryName} />
                      <TableCell className="tabular-nums text-muted-foreground">{formatDate(item.dueDate)}</TableCell>
                      <TableCell>
                        <span className="text-sm font-medium tabular-nums text-destructive">{item.daysOverdue} dias</span>
                      </TableCell>
                      <TableCell><AmountText tone="neutral">{formatCurrency(item.amount)}</AmountText></TableCell>
                      <TableCell>{priority && <Badge variant={priority.variant}>{priority.label}</Badge>}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        ) : (
          <EmptyState>Nenhuma transação vencida</EmptyState>
        )}
      </SectionCard>

      <SectionCard title="Vencendo nos Próximos 7 Dias" icon={Clock}>
        {dashboard?.dueSoon && dashboard.dueSoon.length > 0 ? (
          <div className="min-w-0 overflow-x-auto">
            <Table className="min-w-[640px]">
              <TableHeader>
                <TableRow>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Descrição</TableHead>
                  <TableHead>Vencimento</TableHead>
                  <TableHead>Dias</TableHead>
                  <TableHead>Valor</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {dashboard.dueSoon.slice(0, 20).map((item) => (
                  <TableRow key={item.id}>
                    <ClientCell name={item.clientName} document={item.clientDocument} />
                    <DescriptionCell description={item.description} category={item.categoryName} />
                    <TableCell className="tabular-nums text-muted-foreground">{formatDate(item.dueDate)}</TableCell>
                    <TableCell>
                      <span className="text-sm font-medium tabular-nums">em {item.daysUntilDue} dias</span>
                    </TableCell>
                    <TableCell><AmountText tone="neutral">{formatCurrency(item.amount)}</AmountText></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <EmptyState>Nenhuma transação vencendo em breve</EmptyState>
        )}
      </SectionCard>
    </div>
  );
}
